import React, { useEffect, useState, useCallback } from "react";
import {
  TrendingUp, Activity, Wallet, AlertTriangle, ShoppingCart, ArrowRightLeft, Clock3,
  Loader2, Sparkles, Database, RefreshCcw,
} from "lucide-react";
import { Tile, Badge } from "./Primitives";
import RecommendationCard from "./RecommendationCard";
import SimulateDrawer from "./SimulateDrawer";
import { ENGINES, listRecommendations, enginesOverview } from "../lib/enginesApi";
import { useRole } from "../lib/role.jsx";
import { formatNumber, cn } from "../lib/utils";
import { originFromDataMode } from "../lib/origin.jsx";

const ICON = {
  demand_forecast: TrendingUp, stockout_adjusted: Activity, inventory_health: Wallet,
  stockout_risk: AlertTriangle, replenishment: ShoppingCart, redistribution: ArrowRightLeft, aging: Clock3,
};
const ACCENT = Object.fromEntries(ENGINES.map(e => [e.id, e.accent]));
const LABEL  = Object.fromEntries(ENGINES.map(e => [e.id, e.label]));

// 7-tab panel surfaced inside Inventory Intelligence. Each tab lists the
// pending recommendations from a single engine with Approve / Snooze /
// Reject / Simulate controls (via RecommendationCard).
export default function InventoryEnginesPanel({ branchId }) {
  const { user, branchScope } = useRole();
  const scopeBranch = branchScope?.branchId || branchId || null;
  const [tab, setTab] = useState(ENGINES[0].id);
  const [recs, setRecs] = useState([]);
  const [includeShadow, setIncludeShadow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [simRec, setSimRec] = useState(null);
  const [health, setHealth] = useState(null);

  const load = useCallback(async (engineId, shadow) => {
    setLoading(true);
    try {
      setError(null);
      const [ov, { recommendations }] = await Promise.all([
        enginesOverview(),
        listRecommendations({
          engine: engineId,
          include_shadow: shadow,
          branch_id: scopeBranch,
          limit: 50,
        }),
      ]);
      setHealth(ov);
      recommendations.sort((a, b) => (b.expected_value_php || 0) - (a.expected_value_php || 0));
      setRecs(recommendations.map(decorate));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [scopeBranch]);

  useEffect(() => { load(tab, includeShadow); }, [tab, includeShadow, load]);

  function onChange(updated) {
    setRecs(list => list.map(r => r.id === updated.id ? decorate(updated) : r));
  }

  const engineHealth = health?.engines?.find(e => e.id === tab);
  const totalValue = recs.reduce((s, r) => s + (r.expected_value_php || 0), 0);
  const pending = recs.filter(r => (r.approval?.state || "pending") === "pending");

  return (
    <Tile testid="engines-panel"
          title="Seven-engine recommendations · 1Rotary Inventory Intelligence"
          origin={engineHealth ? {
            origin: originFromDataMode(engineHealth.data_mode),
            source: `Seven-engine daily cycle · ${LABEL[tab]} · ${originFromDataMode(engineHealth.data_mode) === "synthetic" ? "synthetic sample history" : "1Rotary ingested history"}`,
            updatedAt: engineHealth.last_run_at,
          } : undefined}
          action={
            <div className="flex items-center gap-2 text-[11px]">
              <label className="inline-flex items-center gap-1 cursor-pointer text-muted" data-testid="engines-shadow-toggle">
                <input type="checkbox" checked={includeShadow} onChange={e => setIncludeShadow(e.target.checked)}
                       className="accent-primary" />
                Include shadow
              </label>
              <button data-testid="engines-panel-reload" onClick={() => load(tab, includeShadow)}
                      className="px-2 py-0.5 border border-border hover:border-primary hover:text-primary rounded-sm inline-flex items-center gap-1 text-[10.5px]">
                <RefreshCcw size={10} /> Reload
              </button>
            </div>
          }>
      {/* Tabs */}
      <div className="border-b border-border -mx-4 px-2 overflow-x-auto">
        <div className="flex gap-1 min-w-max" data-testid="engines-tablist">
          {ENGINES.map(e => {
            const Icon = ICON[e.id];
            const active = tab === e.id;
            return (
              <button key={e.id} data-testid={`engines-tab-${e.id}`} onClick={() => setTab(e.id)}
                      className={cn("inline-flex items-center gap-1.5 px-3 py-2 text-[11.5px] uppercase tracking-wider font-semibold transition-colors border-b-2 -mb-px",
                        active ? "border-primary text-primary" : "border-transparent text-muted hover:text-fg")}>
                <Icon size={12} /> {e.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Engine health strip */}
      {engineHealth && (
        <div className="flex items-center flex-wrap gap-2 text-[11px] pt-3" data-testid={`engines-health-${tab}`}>
          <Badge status={engineHealth.mode === "live" ? "success" : "info"}>mode · {engineHealth.mode}</Badge>
          {engineHealth.last_run_at && (
            <span className="text-muted">
              Last run · <span className="font-mono-tabular">{engineHealth.last_run_at.slice(5, 16).replace("T", " ")}</span>
              {" · "}{engineHealth.last_duration_ms} ms
            </span>
          )}
          <span className="ml-auto flex items-center gap-3 text-[11px]">
            <span><span className="text-muted">Shown: </span><strong className="font-mono-tabular">{recs.length}</strong></span>
            <span><span className="text-muted">Pending: </span><strong className="font-mono-tabular">{pending.length}</strong></span>
            <span><span className="text-muted">Expected PHP: </span><strong className="font-mono-tabular text-primary">₱{formatNumber(totalValue, { compact: true })}</strong></span>
          </span>
        </div>
      )}

      {/* Content */}
      <div className="pt-3 space-y-2 max-h-[720px] overflow-y-auto -mx-1 px-1">
        {loading ? (
          <div className="py-10 text-center text-muted text-[12.5px]">
            <Loader2 size={16} className="inline animate-spin mr-2" /> Loading {LABEL[tab]}…
          </div>
        ) : error ? (
          <div data-testid="engines-panel-error" className="border border-critical/40 bg-critical/5 text-critical rounded-sm p-3 text-[12px]">
            {error}
          </div>
        ) : recs.length === 0 ? (
          <div className="py-10 text-center text-muted">
            <Sparkles size={18} className="mx-auto mb-2 opacity-60" />
            <div className="text-[13px] font-medium text-fg">No recommendations from {LABEL[tab]}.</div>
            <div className="text-[11.5px] mt-1">
              Trigger a cycle from the Engines Admin console or wait for the daily morning run.
            </div>
          </div>
        ) : (
          recs.map(r => (
            <RecommendationCard key={r.id} rec={r}
                                actor={user?.employeeId || "ui"}
                                onChange={onChange}
                                onSimulate={setSimRec} />
          ))
        )}
      </div>

      <SimulateDrawer open={!!simRec} rec={simRec} onClose={() => setSimRec(null)} />
    </Tile>
  );
}

function decorate(r) {
  return {
    ...r,
    engine_label: r.engine_label || LABEL[r.engine] || r.engine,
    engine_accent: r.engine_accent || ACCENT[r.engine] || "primary",
  };
}
